import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Category, CreateTopicResponse } from '@mmf/contracts';
import { createTopic } from '../api/client';

interface NewTopicFormProps {
  categories: Category[];
  onCreated: (result: CreateTopicResponse) => void | Promise<void>;
  onCancel: () => void;
}

export function NewTopicForm({ categories, onCreated, onCancel }: NewTopicFormProps): React.JSX.Element {
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const mutation = useMutation({
    mutationFn: () => createTopic({ categoryId, title, body }),
    onSuccess: (result) => onCreated(result),
  });

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    mutation.mutate();
  };

  const canSubmit = categoryId !== '' && title.trim() !== '' && body.trim() !== '';

  return (
    <form onSubmit={handleSubmit} className="new-topic-form" aria-labelledby="new-topic-heading">
      <h3 id="new-topic-heading">Start a Discussion</h3>

      {mutation.error && (
        <p role="alert" className="notice warning" data-testid="new-topic-error">
          Could not post that. Try again, and let the service desk know if it persists.
        </p>
      )}

      <label htmlFor="new-topic-category">Category</label>
      <select
        id="new-topic-category"
        value={categoryId}
        required
        onChange={(event) => setCategoryId(event.target.value)}
      >
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </select>

      <label htmlFor="new-topic-title">Title</label>
      <input
        id="new-topic-title"
        value={title}
        maxLength={300}
        required
        onChange={(event) => setTitle(event.target.value)}
      />

      <label htmlFor="new-topic-body">What&apos;s on your mind?</label>
      <textarea
        id="new-topic-body"
        value={body}
        rows={5}
        required
        onChange={(event) => setBody(event.target.value)}
      />

      <div className="row">
        <button
          type="submit"
          className="button"
          disabled={!canSubmit || mutation.isPending}
          data-testid="submit-new-topic"
        >
          {mutation.isPending ? 'Posting…' : 'Post'}
        </button>
        <button type="button" className="button secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
